import { isNotificationUnread } from '../../utils/countUnreadNotifications';
import { useToast } from '../../context/ToastContext';
import { coalesceWorkoutNotifications } from '../../utils/coalesceWorkoutNotifications';
import { useNotificationFeed } from '../../hooks/useNotificationFeed';
import { NotificationLoadMore } from '../../components/NotificationLoadMore';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoCheckmark,
  IoCheckmarkDoneOutline,
  IoPeopleOutline,
  IoSearchOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { NmButton } from '../../components/NmButton';
import { NmCard } from '../../components/NmCard';
import { NmEmptyState } from '../../components/NmEmptyState';
import { NmSkeleton } from '../../components/NmSkeleton';
import { useAthleteContext } from '../../context/AthleteContext';
import { usePageRefresh } from '../../context/PageRefreshContext';
import { useNotificationCount } from '../../hooks/useNotificationCount';
import { WorkoutNotificationSummary } from '../../components/notifications/WorkoutNotificationSummary';
import { WorkoutDetailPanel } from '../../pages/WorkoutDetailPanel';
import {
  notificationsApi,
  type AppNotification,
  type CoachAthleteRequestItem,
} from '../../services/api';
import {
  isEventNotification,
  isMetricNotification,
  isWorkoutNotification,
  notificationWorkoutToCalendar,
  toGregorianDateParam,
  wasUnread,
} from './notificationMappers';
import { isRtlLocale } from '../../i18n/config';
import {
  buildWorkoutNotificationCopy,
  formatNotificationDateLabel,
  inferWorkoutNotificationKind,
} from '../../utils/notificationDisplay';

function NotificationSkeletonList() {
  return (
    <div className="notification-skeleton-list" aria-hidden>
      {Array.from({ length: 4 }, (_, index) => (
        <div className="notification-skeleton" key={index} style={{ animationDelay: `${index * 90}ms` }}>
          <div className="notification-skeleton__meta"><NmSkeleton width={72} height={10}/><NmSkeleton width={88} height={10}/></div>
          <div className="notification-skeleton__card">
            <NmSkeleton width={34} height={34} radius="50%"/>
            <div className="notification-skeleton__copy"><NmSkeleton width="72%" height={13}/><NmSkeleton width="92%" height={9}/><NmSkeleton width="58%" height={9}/></div>
            <NmSkeleton className="nm-skeleton--dark" width="46%" height={82} radius={12}/>
          </div>
        </div>
      ))}
    </div>
  );
}
import '../../styles/notification-center.css';

interface Props {
  isCoach: boolean;
}

function isCoachRequest(
  item: AppNotification,
): item is AppNotification & { notifiable: CoachAthleteRequestItem } {
  return (
    item.notifiable_type === 'CoachAthleteRequest' &&
    typeof item.notifiable === 'object' &&
    item.notifiable != null &&
    'status' in item.notifiable
  );
}

function canRespondToRequest(isCoach: boolean, req: CoachAthleteRequestItem): boolean {
  if (req.status !== 'pending') return false;
  const initiatedBy = req.initiated_by ?? 'coach';
  return isCoach ? initiatedBy === 'athlete' : initiatedBy === 'coach';
}

function notificationDescription(
  description: string | null | undefined,
  fallback: string,
): string {
  const trimmed = description?.trim();
  return trimmed ? trimmed : fallback;
}

/** Notification inbox — mirrors Ai-Tempo `Notification.tsx` mobile layout. */
export function MobileNotificationPage({ isCoach }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const rtl = isRtlLocale(locale);
  const navigate = useNavigate();
  const { effectiveUserId, athletes } = useAthleteContext();
  const { groups, setGroups, markFeedRead, loadFirst, loadMore, hasMore, loadingMore, moreError } = useNotificationFeed(!isCoach);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<number | null>(null);
  const [clearing, setClearing] = useState(false);
  const toast = useToast();
  const [recordSection, setRecordSection] = useState<'records' | undefined>();
  const [detailAthleteId, setDetailAthleteId] = useState<number | undefined>();
  const [detailWorkoutId, setDetailWorkoutId] = useState<number | null>(null);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<number[]>([]);
  const { unreadCount, refreshUnreadCount, adjustUnreadCount } = useNotificationCount();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadFirst();
      // The badge refresh must not keep an already-loaded inbox behind skeletons.
      void refreshUnreadCount().catch(() => {});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('notifications.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [loadFirst, t, refreshUnreadCount]);

  useEffect(() => {
    void load();
  }, [load]);

  usePageRefresh(load, [load]);

  const hasUnreadActivity = Object.values(groups).some(items => items.some(item =>
    item.notifiable_type !== 'CoachAthleteRequest' && isNotificationUnread(item.is_read),
  ));

  const flatCount = useMemo(
    () => Object.values(groups).reduce((sum, arr) => sum + arr.length, 0),
    [groups],
  );

  const visibleGroups = useMemo(() => {
    const activityGroups = coalesceWorkoutNotifications(groups);
    if (!isCoach || selectedAthleteIds.length === 0) return activityGroups;
    const selected = new Set(selectedAthleteIds);
    return Object.fromEntries(
      Object.entries(activityGroups)
        .map(([date, items]) => [
          date,
          items.filter((item) => {
            const athleteId = notificationAthleteId(item);
            return athleteId != null && selected.has(athleteId);
          }),
        ] as const)
        .filter(([, items]) => items.length > 0),
    );
  }, [groups, isCoach, selectedAthleteIds]);

  const visibleCount = useMemo(
    () => Object.values(visibleGroups).reduce((sum, items) => sum + items.length, 0),
    [visibleGroups],
  );

  const markNotificationRead = useCallback(
    async (item: AppNotification) => {
      const unread = wasUnread(item);
      setGroups((prev) => Object.fromEntries(
        Object.entries(prev)
          .map(([date, items]) => [date, items.filter((entry) => entry.id !== item.id)] as const)
          .filter(([, items]) => items.length > 0),
      ));
      if (!unread) return;
      adjustUnreadCount(-1);
      try {
        await notificationsApi.markAsRead(!isCoach, [item.id]);
      } catch {
        // Restore the server-backed inbox if the optimistic dismissal failed.
        await load();
      }
    },
    [adjustUnreadCount, isCoach, load],
  );

  const handleNotificationOpen = useCallback(
    async (item: AppNotification) => {
      if (isWorkoutNotification(item)) {
        await markNotificationRead(item);
        setRecordSection(inferWorkoutNotificationKind(item.description, item.workout_kind) === 'workout_record_broken' ? 'records' : undefined);
        setDetailAthleteId(notificationAthleteId(item) ?? undefined);
        setDetailWorkoutId(Number(item.notifiable.id));
        return;
      }

      if (isEventNotification(item)) {
        await markNotificationRead(item);
        const dateParam = toGregorianDateParam(item.notifiable.event_date);
        navigate(`/calendar/event/new?date=${dateParam}&id=${item.notifiable.id}`);
        return;
      }

      if (isMetricNotification(item)) {
        await markNotificationRead(item);
        const dateParam = toGregorianDateParam(item.notifiable.date);
        const code = item.notifiable.unique_code;
        if (code) {
          navigate(`/calendar/metric/new?date=${dateParam}&code=${encodeURIComponent(code)}`);
        } else {
          navigate(`/calendar/metric/new?date=${dateParam}`);
        }
        return;
      }

      if (!isCoachRequest(item)) {
        await markNotificationRead(item);
      }
    },
    [markNotificationRead, navigate],
  );

  async function handleAccept(requestId: number, asCoach: boolean) {
    setActingId(requestId);
    try {
      if (asCoach) {
        await notificationsApi.acceptCoachRequestAsCoach(requestId);
      } else {
        await notificationsApi.acceptCoachRequest(requestId);
      }
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('notifications.acceptFailed'));
    } finally {
      setActingId(null);
    }
  }

  async function handleDecline(requestId: number, asCoach: boolean) {
    setActingId(requestId);
    try {
      if (asCoach) {
        await notificationsApi.declineCoachRequestAsCoach(requestId);
      } else {
        await notificationsApi.declineCoachRequest(requestId);
      }
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('notifications.declineFailed'));
    } finally {
      setActingId(null);
    }
  }

  async function handleReadAll() {
    if (clearing || flatCount === 0) return;
    setClearing(true);
    setError('');
    try {
      await notificationsApi.markAllRead(!isCoach);
      markFeedRead();
      toast.success(t('notificationRead.allRead'));
      void refreshUnreadCount().catch(() => {});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('notifications.clearFailed'));
    } finally {
      setClearing(false);
    }
  }

  return (
    <>
      <div className="mobile-screen">
        <div className="notification-page__topbar">
          <h1 className="nm-section-title">{t('notifications.title')}</h1>
          <div className="notification-page__actions">
            {isCoach && athletes.length > 1 && (
              <AthleteNotificationFilter
                athletes={athletes}
                selectedIds={selectedAthleteIds}
                onChange={setSelectedAthleteIds}
              />
            )}
            <NmButton
              variant="secondary"
              size="sm"
              className="notification-page__read-all"
              loading={clearing}
              disabled={loading || clearing || flatCount === 0 || (!hasUnreadActivity && unreadCount === 0)}
              aria-label={t('notifications.readAll')}
              title={t('notifications.readAll')}
              onClick={() => void handleReadAll()}
            >
              <IoCheckmarkDoneOutline size={15} aria-hidden />
              <span>{t(!hasUnreadActivity && unreadCount === 0 && flatCount > 0 ? 'notificationRead.done' : 'notifications.readAllShort')}</span>
            </NmButton>
          </div>
        </div>
        <p className="mobile-screen__subtitle">
          {isCoach ? t('notifications.subtitleCoach') : t('notifications.subtitleAthlete')}
        </p>

        {!isCoach && (
          <div className="mobile-toolbar-row">
            <NmButton variant="ghost" size="sm" onClick={() => navigate('/settings/notifications')}>
              {t('notifications.settings', 'Settings')}
            </NmButton>
          </div>
        )}

        {error && (
          <NmCard variant="inset" style={{ marginBottom: 12, color: 'var(--color-danger)' }}>
            <div role="alert">{error}</div>
            <NmButton variant="ghost" size="sm" onClick={() => void load()}>
              {t('common.retry', 'Retry')}
            </NmButton>
          </NmCard>
        )}

        {loading ? (
          <NotificationSkeletonList />
        ) : error ? null : visibleCount === 0 ? (
          <NmEmptyState>🔔 {t('notifications.empty')}</NmEmptyState>
        ) : (
          Object.entries(visibleGroups).map(([dateKey, items]) => (
            <section key={dateKey} className="notif-section">
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  {!isWorkoutNotification(item) && <h2 className={`notif-section__date${rtl ? ' notif-section__date--rtl' : ''}`}>{formatNotificationDateLabel(dateKey, locale)}</h2>}
                  <NotificationCard item={item} isCoach={isCoach} acting={actingId === (isCoachRequest(item) ? item.notifiable.id : null)} onAccept={handleAccept} onDecline={handleDecline} onOpen={() => void handleNotificationOpen(item)} athletes={athletes} dateLabel={formatNotificationDateLabel(dateKey, locale)} rtl={rtl} />
                </React.Fragment>
              ))}
            </section>
          ))
        )}
      </div>

      <NotificationLoadMore hasMore={!loading && hasMore} loading={loadingMore} failed={moreError} onLoad={loadMore} itemCount={flatCount} />
      {detailWorkoutId != null && effectiveUserId != null && (
        <WorkoutDetailPanel
          workoutId={detailWorkoutId}
          initialSection={recordSection}
          userId={detailAthleteId ?? effectiveUserId}
          onClose={() => setDetailWorkoutId(null)}
        />
      )}
    </>
  );
}

function notificationAthleteId(item: AppNotification): number | null {
  const notifiable = item.notifiable;
  if (!notifiable || typeof notifiable !== 'object') return null;
  const record = notifiable as Record<string, unknown>;
  const direct = Number(record.user_id ?? record.athlete_id);
  if (Number.isFinite(direct) && direct > 0) return direct;
  for (const key of ['user', 'athlete']) {
    const nested = record[key];
    if (!nested || typeof nested !== 'object') continue;
    const id = Number((nested as Record<string, unknown>).id);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return null;
}

function AthleteNotificationFilter({
  athletes,
  selectedIds,
  onChange,
}: {
  athletes: Array<{ id: number; full_name: string; profile_image?: string }>;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleAthletes = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return athletes;
    return athletes.filter((athlete) => athlete.full_name.toLocaleLowerCase().includes(needle));
  }, [athletes, query]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggle = (id: number) => {
    onChange(selected.has(id) ? selectedIds.filter((value) => value !== id) : [...selectedIds, id]);
  };

  return (
    <div ref={rootRef} className="notification-athlete-filter notification-athlete-filter--mobile">
      <button
        type="button"
        className={`notification-athlete-filter__trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('notifications.filter.label')}
        title={t('notifications.filter.label')}
      >
        <IoPeopleOutline aria-hidden />
        {selectedIds.length > 0 && (
          <span className="notification-athlete-filter__count">{selectedIds.length}</span>
        )}
      </button>

      {open && (
        <div className="notification-athlete-filter__popover">
          <label className="notification-athlete-filter__search">
            <IoSearchOutline aria-hidden />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('notifications.filter.search')}
            />
          </label>
          <button
            type="button"
            className={`notification-athlete-filter__option${selectedIds.length === 0 ? ' is-selected' : ''}`}
            onClick={() => onChange([])}
          >
            <span className="notification-athlete-filter__all"><IoPeopleOutline aria-hidden /></span>
            <span>{t('notifications.filter.all')}</span>
            {selectedIds.length === 0 && <IoCheckmark aria-hidden />}
          </button>
          <div className="notification-athlete-filter__list" role="listbox" aria-multiselectable="true">
            {visibleAthletes.map((athlete) => (
              <button
                type="button"
                role="option"
                aria-selected={selected.has(athlete.id)}
                key={athlete.id}
                className={`notification-athlete-filter__option${selected.has(athlete.id) ? ' is-selected' : ''}`}
                onClick={() => toggle(athlete.id)}
              >
                {athlete.profile_image ? (
                  <img src={athlete.profile_image} alt="" />
                ) : (
                  <span className="notification-athlete-filter__fallback">
                    {athlete.full_name.trim().charAt(0)}
                  </span>
                )}
                <span>{athlete.full_name}</span>
                {selected.has(athlete.id) && <IoCheckmark aria-hidden />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationCard({
  item,
  isCoach,
  acting,
  onAccept,
  onDecline,
  onOpen,
  athletes,
  dateLabel,
  rtl,
}: {
  item: AppNotification;
  isCoach: boolean;
  acting: boolean;
  onAccept: (id: number, asCoach: boolean) => void;
  onDecline: (id: number, asCoach: boolean) => void;
  onOpen: () => void;
  athletes: Array<{ full_name: string; profile_image?: string }>;
  dateLabel: string;
  rtl: boolean;
}) {
  const { t, i18n } = useTranslation();
  const unread = wasUnread(item);

  if (isCoachRequest(item)) {
    const req = item.notifiable;
    const initiatedBy = req.initiated_by ?? 'coach';
    const responder = canRespondToRequest(isCoach, req);

    if (req.status === 'pending' && responder) {
      const displayName =
        initiatedBy === 'athlete'
          ? (req.athlete?.full_name ?? t('notifications.athleteFallback'))
          : (req.coach?.full_name ?? t('notifications.coachFallback'));
      const bodyKey =
        initiatedBy === 'athlete' ? 'notifications.athleteWantsJoin' : 'notifications.coachWantsAdd';

      return (
        <NmCard variant="raised-sm" className={`notif-card${unread ? ' notif-card--unread' : ''}`}>
          <span className="notif-card__badge">{t('notifications.coachRequest')}</span>
          <p className="notif-card__text">{t(bodyKey, { name: displayName })}</p>
          {item.description?.trim() ? (
            <p className="notif-card__desc">{item.description.trim()}</p>
          ) : null}
          <div className="notif-card__actions">
            <NmButton variant="primary" size="sm" loading={acting} onClick={() => onAccept(req.id, isCoach)}>
              {t('notifications.accept')}
            </NmButton>
            <NmButton variant="ghost" size="sm" disabled={acting} onClick={() => onDecline(req.id, isCoach)}>
              {t('notifications.decline')}
            </NmButton>
          </div>
        </NmCard>
      );
    }

    return (
      <button
        type="button"
        className={`notif-card-btn${unread ? ' notif-card-btn--unread' : ''}`}
        onClick={onOpen}
      >
        <NmCard variant="inset" className="notif-card">
          <span className="notif-card__badge notif-card__badge--muted">
            {t('notifications.coachRequest')} · {req.status}
          </span>
          <p className="notif-card__text">
            {notificationDescription(item.description, t('notifications.requestUpdated'))}
          </p>
        </NmCard>
      </button>
    );
  }

  if (isWorkoutNotification(item)) {
    const workout = notificationWorkoutToCalendar(item.notifiable);
    const copy = buildWorkoutNotificationCopy(t, item, {
      isCoachViewer: isCoach,
      locale: i18n.language,
    });
    const workoutKind = inferWorkoutNotificationKind(item.description, item.workout_kind) ?? 'workout_completed';
    const athleteImage = copy.athleteImage
      || athletes.find((athlete) => athlete.full_name.trim() === copy.athleteName)?.profile_image
      || '';

    return (
      <div className={`notif-workout${unread ? ' notif-workout--unread' : ''}`}>
        <div className={`notification-card-meta${rtl ? ' is-rtl' : ''}`}>
          <span className="notification-card-meta__date">{dateLabel}</span>
          {copy.athleteName && <span className="notification-workout-summary__athlete">{athleteImage && <img src={athleteImage} alt=""/>}<span>{copy.athleteName}</span></span>}
        </div>
        {workoutKind !== 'workout_completed' && workoutKind !== 'workout_record_broken' && <p className="notif-workout__lead"><span className="notif-card__badge">{copy.badge}</span>{copy.message}</p>}
        {workoutKind === 'workout_record_broken' && <button type="button" onClick={onOpen} style={{ width: '100%', textAlign: 'start', padding: '12px 0', background: 'none', border: 0, color: 'inherit', cursor: 'pointer' }}><strong>{copy.message}</strong><small style={{ display: 'block', marginTop: 4 }}>{t('annualRecords.viewRecords')}</small></button>}
        <WorkoutNotificationSummary workout={workout} rawWorkout={item.notifiable} isRecord={workoutKind === 'workout_record_broken'} onOpenWorkout={() => onOpen()} />
      </div>
    );
  }

  if (isEventNotification(item)) {
    const actorName = item.user_id?.full_name;
    return (
      <button
        type="button"
        className={`notif-card-btn${unread ? ' notif-card-btn--unread' : ''}`}
        onClick={onOpen}
      >
        <NmCard variant="raised-sm" className="notif-card">
          <span className="notif-card__badge notif-card__badge--muted">{t('notifications.event', 'Event')}</span>
          {(actorName || item.description?.trim()) && (
            <p className="notif-card__desc">
              {[actorName, item.description?.trim()].filter(Boolean).join(' ')}
            </p>
          )}
          <p className="notif-card__text">{item.notifiable.name ?? notificationDescription(item.description, t('notifications.genericUpdate'))}</p>
        </NmCard>
      </button>
    );
  }

  if (isMetricNotification(item)) {
    const actorName = item.user_id?.full_name;
    const metricLabel = item.notifiable.metric_type_name
      ? `${item.notifiable.metric_type_name}: ${item.notifiable.value ?? ''}`
      : null;
    return (
      <button
        type="button"
        className={`notif-card-btn${unread ? ' notif-card-btn--unread' : ''}`}
        onClick={onOpen}
      >
        <NmCard variant="raised-sm" className="notif-card">
          <span className="notif-card__badge notif-card__badge--muted">{t('notifications.metric', 'Metric')}</span>
          {(actorName || item.description?.trim()) && (
            <p className="notif-card__desc">
              {[actorName, item.description?.trim()].filter(Boolean).join(' ')}
            </p>
          )}
          {metricLabel ? <p className="notif-card__text">{metricLabel}</p> : null}
        </NmCard>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`notif-card-btn${unread ? ' notif-card-btn--unread' : ''}`}
      onClick={onOpen}
    >
      <NmCard variant="raised-sm" className="notif-card">
        <span className="notif-card__badge notif-card__badge--muted">{item.notifiable_type ?? 'Update'}</span>
        <p className="notif-card__text">
          {notificationDescription(item.description, t('notifications.genericUpdate'))}
        </p>
      </NmCard>
    </button>
  );
}
